import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { diffLines, foldUnchanged } from '../utils/diffLines';

const ROW_STYLES = {
  added: { bgcolor: 'rgba(0, 212, 170, 0.10)', color: '#00d4aa', prefix: '+' },
  removed: { bgcolor: 'rgba(239, 68, 68, 0.10)', color: '#f87171', prefix: '-' },
  same: { bgcolor: 'transparent', color: 'text.secondary', prefix: ' ' }
};

/** Nombre de lignes qui changent entre deux textes. */
export const countChanges = (before, after) =>
  diffLines(before, after).filter((row) => row.type !== 'same').length;

/**
 * Diff ligne à ligne entre deux textes.
 *
 * Partagé par l'historique d'un document et la validation des propositions de
 * l'agent : les deux montrent la même chose, elles doivent la montrer pareil.
 *
 * `contextLines` replie les plages inchangées ; `null` affiche tout, ce que
 * veut l'historique où l'on lit une version entière.
 */
export function DiffView({ before, after, contextLines = null, sx = {} }) {
  const rows = useMemo(
    () => foldUnchanged(diffLines(before, after), contextLines),
    [before, after, contextLines]
  );

  return (
    <Box sx={{ overflow: 'auto', bgcolor: 'rgba(0,0,0,0.2)', p: 1.5, ...sx }}>
      {rows.map((row, index) => {
        if (row.type === 'fold') {
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 1,
                fontFamily: 'monospace',
                fontSize: '0.72rem',
                color: 'text.secondary',
                px: 0.75,
                py: 0.25,
                userSelect: 'none'
              }}
            >
              {/* Espace masqué de la largeur d'un préfixe de ligne (+/-/ ) : sans
                  lui le texte du marqueur démarre avant celui des lignes de diff,
                  qui décalent le leur du préfixe plus ce même gap. `fontSize`
                  explicite à celui des lignes (pas celui, plus petit, du
                  marqueur) : sans lui la colonne réservée n'a pas exactement la
                  largeur du préfixe qu'elle doit imiter. */}
              <Box component="span" sx={{ visibility: 'hidden', fontSize: '0.78rem' }}>+</Box>
              <Box component="span">⋯ {row.count} ligne(s) inchangée(s)</Box>
            </Box>
          );
        }

        const style = ROW_STYLES[row.type];
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 1,
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              bgcolor: style.bgcolor,
              color: style.color,
              px: 0.75,
              borderRadius: 0.5
            }}
          >
            <Box component="span" sx={{ opacity: 0.6, userSelect: 'none' }}>{style.prefix}</Box>
            <Box component="span" sx={{ flex: 1 }}>{row.text || ' '}</Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default DiffView;
